import Swal from "sweetalert2";
import * as Cesium from 'cesium';
import { useEffect } from "react";
import AssetName from "./AssetName";
import styled from "styled-components";
import TextButton from "components/TextButton";
import React, { useState, useRef } from "react";
import StacInterface from "utils/StacInterface";
import { createMetadata } from "@/utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import withReactContent from "sweetalert2-react-content";
import { styled as muiStyled } from '@mui/material/styles';
import TooltipIconButton from "components/TooltipIconButton";
import useViewerStore from "../../../../store-zustand/viewer";
import SidebarHeader from "components/Sidebar/layout/SidebarHeader";
import { CircularProgress, Slider, Select, MenuItem } from "@mui/material";
import { ChevronLeft, ChevronRight, ArrowBack } from "@mui/icons-material";

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 30px;
`;

const MarketplaceSlider = muiStyled(Slider)(({ theme }) => ({
  color: "#487BF2", 
  marginRight: 10, 
  marginLeft: 10,
}));

const Title = styled.h2`
  color: white;
`;

const Border = styled.div`
  border-bottom: 1px solid #707173;
  margin: 10px 0;
`;

const SubTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  h3 {
    font-family: Roboto;    
    font-weight: 400;
    font-size: 20px;
    line-height: 23px;
    color: #E5E5E5;
  }

  p {
    font-family: Roboto;    
    font-weight: 500;
    font-size: 13px;
    line-height: 15px;
    color: #C4C4C4;
  }
`;

const DownloadItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px;

  &:hover {
    background-color: #262626;
  }

  span {
    font-family: Roboto;    
    font-weight: 500;
    font-size: 15px;
    line-height: 23px;
    color: #A0A0A0;

    &.selected {
      color: #E5E5E5;
    }
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const DownloadList = styled.div`
  height: 54vh;
  overflow-y: scroll;

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: #707173;
  }
  
  &::-webkit-scrollbar {
    width: 6px;
    background-color: #2F2F2F;
  }
`;

const CollectionDetail = ({ collection, onClose, onBack }) => {
  const dispatch = useDispatch();
  const { setmapboxvisibility } = useViewerStore();
  const [items, setItems] = useState([]);
  const [maxDate, setMaxDate] = useState();
  const [minDate, setMinDate] = useState();
  const [loader, setLoader] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [selectedItemIDs, setSelectedItemIDs] = useState([]);
  const [itemsStartOffset, setItemsStartOffset] = useState(0);
  const [yearSliderValue, setYearSliderValue] = useState([0, 100]);
  const [currentCollectionRef, setCurrentCollectionRef] = useState();
  const notifyDownload = useSelector(state => state.asset.notifyDownload);
  const currentCollection = useSelector(state => state.asset.currentCollection);
  const downloadPolygonAdded = useSelector(state => state.asset.downloadPolygonAdded);
  const selectedItemIDsRef = useRef(selectedItemIDs);
  const yearSliderValueRef = useRef(yearSliderValue);
  const updateListItemsRef = useRef(null);
  const moveEndListenerRef = useRef(null);
  if (!moveEndListenerRef.current) {
    moveEndListenerRef.current = () => updateListItemsRef.current?.();
  }

  const showErrorModal = (error_message) => {
    withReactContent(Swal).fire({
      icon: 'error',
      title: 'Error',
      html : `${error_message}`
    });
  };

  const sliderTextValue = (value) => {
    return ((value / 100) * (maxDate - minDate)) + minDate
  }

  const nextPage = () => {
    if (itemsStartOffset + itemsPerPage < totalItems) setItemsStartOffset(itemsStartOffset + itemsPerPage);
  }

  const prevPage = () => {
    if (itemsStartOffset - itemsPerPage >= 0) setItemsStartOffset(itemsStartOffset - itemsPerPage);
  }

  const download = () => {
    if (selectedItemIDs.length === 0) return;
    
    withReactContent(Swal).fire({
      icon: 'info',
      title: 'Info',
      html: 'Please contact sales with a list of your desired items for download: <a href="mailto:sales@fastmap.ca">sales@fastmap.ca</a>',
    });
  }

  const handleItemCheckbox = (item) => {
    if (selectedItemIDs.includes(item)) {
      setSelectedItemIDs(selectedItemIDs.filter((uid) => uid !== item));
    } else {
      setSelectedItemIDs([...selectedItemIDs, item]);
    }
  }

  const handleItemClick = (item) => {
    StacInterface.instance.getItem(item).then((response) => {
      let info = createMetadata(response.data)
      dispatch({ type: 'SET_ASSET_METADATA', assetMetaData: info });
      window.MAIN_CESIUM_VIEWER.entities.remove(window.selectedPolygon);
      loadItemFootprint(item, false).then((polygon) => {
        window.selectedPolygon = polygon;
      });
    }).catch(() => showErrorModal('Error getting item STAC.'))

    dispatch({ type: 'TOGGLE_METADATA_MODAL', show: true });
  }

  const handleYearChange = (evt, value) => {
    setYearSliderValue(value);
  }

  const setListItems = (start_year, end_year, west, south, east, north, checked) => {
    setLoader(true);
    StacInterface.instance.searchAssets(start_year + "-01-01", end_year + 1 + "-01-01", west, south, east, north, itemsStartOffset, itemsStartOffset + itemsPerPage, currentCollectionRef.title.replace(" ", "-")).then((response) => {
      setTotalItems(response.data.total);
      
      let newItems = response.data.uuids.map(item => { return item })
      setItems(newItems);
      if (checked) {
        var newSelectedItems = [...selectedItemIDsRef.current];
        newItems.forEach((item) => {
          if (!newSelectedItems.includes(item)) {
            newSelectedItems.push(item);
          }
        })
        setSelectedItemIDs(newSelectedItems);
      }
      setLoader(false);
    }).catch((error) => {
      if (error.__CANCEL__) return;
      showErrorModal('Error getting filtered items.')}
    )
  }

  const updateListItems = () => {
    if (minDate === undefined || maxDate === undefined) return;

    let itemsPolygonExist = (window.itemsPolygon !== undefined);
    let west, south, north, east;
    if (itemsPolygonExist) {
      var coords = window.itemsPolygon.rectangle.coordinates._value;
      if (!coords) return;
      west = coords.west * Cesium.Math.DEGREES_PER_RADIAN;
      south = coords.south * Cesium.Math.DEGREES_PER_RADIAN;
      east = coords.east * Cesium.Math.DEGREES_PER_RADIAN;
      north = coords.north * Cesium.Math.DEGREES_PER_RADIAN;
    } else {
      var cameraBounds = window.MAIN_CESIUM_VIEWER.camera.computeViewRectangle(Cesium.Ellipsoid.WGS84);
      west = cameraBounds.west * Cesium.Math.DEGREES_PER_RADIAN;
      south = cameraBounds.south * Cesium.Math.DEGREES_PER_RADIAN;
      east = cameraBounds.east * Cesium.Math.DEGREES_PER_RADIAN;
      north = cameraBounds.north * Cesium.Math.DEGREES_PER_RADIAN;
    }

    let start_year = ((yearSliderValueRef.current[0] / 100) * (maxDate - minDate)) + minDate;
    let end_year = ((yearSliderValueRef.current[1] / 100) * (maxDate - minDate)) + minDate;
    if (!west || !south || !east || !north) return;
    setListItems(start_year, end_year, west, south, east, north, itemsPolygonExist);
  }
  updateListItemsRef.current = updateListItems;

  const resetListener = () => {
    removeListener();
    addListener();
  }

  const addListener = () => {
    const listener = moveEndListenerRef.current;
    const moveEnd = window.MAIN_CESIUM_VIEWER?.camera?.moveEnd;
    if (!moveEnd || !listener) return;

    moveEnd.addEventListener(listener);
    window.collectionDetailMoveEndListener = listener;
  }

  const removeListener = () => {
    const listener = moveEndListenerRef.current;
    const moveEnd = window.MAIN_CESIUM_VIEWER?.camera?.moveEnd;
    if (!moveEnd || !listener) return;

    moveEnd.removeEventListener(listener);
    delete window.collectionDetailMoveEndListener;
  }

  const handleBack = () => {
    removeListener();

    if (window.polygons) {
      Object.keys(window.polygons).forEach((id) => {
        window.MAIN_CESIUM_VIEWER.entities.remove(window.polygons[id]);
      })
      delete (window.polygons);
    }

    if (window.selectedPolygon) window.MAIN_CESIUM_VIEWER.entities.remove(window.selectedPolygon);
    dispatch({ type: 'TOGGLE_DATA_DOWNLOAD_MODAL', show: false });
    
    if (window.itemsPolygon) {
      window.MAIN_CESIUM_VIEWER.entities.remove(window.itemsPolygon);
      delete (window.itemsPolygon);
    }
    dispatch({ type: 'SET_DOWNLOAD_POLYGON', downloadPolygonAdded: false });
    
    setmapboxvisibility(0);
    onBack();
  }

  const handleClose = () => {
    handleBack();
    onClose();
  }

  const updateDateRange = () => {
    StacInterface.instance.getDates(-180, -90, 180, 90, currentCollectionRef.title.replace(" ", "-")).then(resp => {
      let min_date = 1990;
      let max_date = new Date().getFullYear()
      if (resp.data !== null || resp.data !== undefined){
        min_date = new Date(resp.data.oldest).getFullYear();
        max_date = new Date(resp.data.newest).getFullYear();
      }

      setMinDate(min_date);
      setMaxDate(max_date);
    }).catch(() => showErrorModal('Error in getting the date range of items in this collection.'))
  }

  const loadItemFootprint = async (item, selected_for_download) => {
    const response = await StacInterface.instance.getItemEnvelop(item);
    if (response.status !== 200) { 
      showErrorModal('Error getting item footprint.')
      return null;
    }
      
    let parsedCoords = [];
    if (typeof response.data.geometry === 'string') {
      parsedCoords = JSON.parse(response.data).coordinates[0];
    } else {
      parsedCoords = response.data.coordinates[0];
    }

    const coords = [];
    for (let i = 0; i < parsedCoords.length - 1; i++) {
      coords.push(parsedCoords[i][0]);
      coords.push(parsedCoords[i][1]);
    }

    let footprint = null;
    const isPoint = parsedCoords.every((coord) => coord[0] === parsedCoords[0][0] && coord[1] === parsedCoords[0][1]);
    if (isPoint) {
      footprint = window.MAIN_CESIUM_VIEWER.entities.add({
        position: Cesium.Cartesian3.fromDegrees(coords[0], coords[1], 5),
        point: {
          pixelSize: 12,
          color: selected_for_download
            ? new Cesium.Color(21 / 255, 198 / 255, 125 / 255, 0.2)
            : new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
          outlineColor: selected_for_download
            ? new Cesium.Color.fromCssColorString('#15C67D')
            : new Cesium.Color.fromCssColorString('#487BF2'),
          outlineWidth: 1.5,
        },
      });
    } else {
      footprint = window.MAIN_CESIUM_VIEWER.entities.add({
        polygon: {
          hierarchy: Cesium.Cartesian3.fromDegreesArray(coords),
          perPositionHeight: false,
          material: selected_for_download
            ? new Cesium.Color(21 / 255, 198 / 255, 125 / 255, 0.2)
            : new Cesium.Color(72 / 255, 123 / 255, 242 / 255, 0.2),
        },
        polyline: {
          positions: Cesium.Cartesian3.fromDegreesArray([...coords, coords[0], coords[1]]),
          material: selected_for_download
            ? new Cesium.Color.fromCssColorString('#15C67D')
            : new Cesium.Color.fromCssColorString('#487BF2'),
          width: 1.5,
          clampToGround: true,
        },
      });
    }
    return footprint;
  }

  useEffect(() => {
    if (yearSliderValue === undefined) return;
    
    yearSliderValueRef.current = yearSliderValue;
    updateListItems();
  }, [yearSliderValue])
  
  useEffect(() => {
    if (minDate === undefined || maxDate === undefined) return;
    
    if (minDate !== maxDate) {
      setYearSliderValue([(100 / (maxDate - minDate)) * (2015 - minDate), 100]);
    } else {
      setYearSliderValue([0, 100]);
    }
    resetListener();
  }, [minDate, maxDate])

  useEffect(() => {
    if (currentCollectionRef === undefined) return;
    
    setSelectedItemIDs([]);
    setItems([]);
    setMinDate();
    setMaxDate();
    updateDateRange();
  }, [currentCollectionRef])

  useEffect(() => {
    if (currentCollection === undefined) return;
    
    setCurrentCollectionRef(currentCollection);
  }, [currentCollection])

  useEffect(() => {
    if (window.polygons === undefined) window.polygons = {};
    
    dispatch({ type: 'TOGGLE_METADATA_MODAL', show: false });
    window.MAIN_CESIUM_VIEWER.entities.remove(window.selectedPolygon);
    selectedItemIDsRef.current = selectedItemIDs;
    
    let polygonKeys = Object.keys(window.polygons);
    const itemsToRemoveFootprints = polygonKeys.filter((item) => !selectedItemIDs.includes(item));
    itemsToRemoveFootprints.forEach((item) => {
      window.MAIN_CESIUM_VIEWER.entities.remove(window.polygons[item]);
      delete (window.polygons[item]);
      StacInterface.instance.getItem(item).then((response) => {
        setTotalSize(totalSize - parseFloat(response?.data?.properties?.size_gb))
      }).catch(() => showErrorModal('Error getting file size.'))
    });

    polygonKeys = Object.keys(window.polygons);
    const itemsToLoadFootprints = selectedItemIDs.filter((item) => !polygonKeys.includes(item));
    void Promise.all(
      itemsToLoadFootprints.map(async (item) => {
        const footprint = await loadItemFootprint(item, true);
        if (footprint) {
          window.polygons[item] = footprint;
          StacInterface.instance.getItem(item).then((response) => {
            setTotalSize(prev => prev + parseFloat(response?.data?.properties?.size_gb));
          }).catch(() => showErrorModal('Error getting file size.'))
        }
      })
    );
    dispatch({ type: 'DOWNLOAD_STARTED', downloadStarted: false });    
  }, [selectedItemIDs])

  useEffect(() => {
    updateListItems();
  }, [itemsPerPage, itemsStartOffset])

  useEffect(() => {
    if (downloadPolygonAdded) {
      removeListener();
    } else {
      if (window.polygons) {
        Object.keys(window.polygons).forEach((id) => {
          window.MAIN_CESIUM_VIEWER.entities.remove(window.polygons[id]);
        });
   
        delete (window.polygons);
        setTotalSize(0);
      }
      setSelectedItemIDs([]);
      resetListener();
    }
    updateListItems();
  }, [downloadPolygonAdded])

  useEffect(() => {
    if (notifyDownload) download();
  }, [notifyDownload])

  useEffect(() => {
    dispatch({ type: 'SET_SELECTED_ASSETS_SIZE', selectedAssetsTotalSize: totalSize });
  }, [totalSize])

  return (
    <>
      <SidebarHeader onClose={handleClose}>
        <TooltipIconButton title="Back" ariaLabel="back" placement="bottom" onClick={handleBack} size={44}>
          <ArrowBack />
        </TooltipIconButton>
      </SidebarHeader>
      <TitleSection>
        <Title>{collection.title} Items</Title>
        <TextButton text="Download" onClick={download} disabled={selectedItemIDs.length === 0}/>
      </TitleSection>
      <AssetName />
      {minDate && maxDate ?
        <div style={{ width: "100%", display: "flex", margin: " 0 0 3rem" }}>
          <div style={{ background: "#232323", borderRadius: 3, width: 50, padding: 5, fontSize: "1.5rem" }}>
            {sliderTextValue(yearSliderValue[0])}
          </div>
          <MarketplaceSlider disabled={minDate === maxDate} value={yearSliderValue} step={100 / (maxDate - minDate)} onChange={handleYearChange} className="marketplace-slider"/>
          <div style={{ background: "#232323", borderRadius: 3, width: 50, padding: 5, fontSize: "1.5rem" }}>
            {sliderTextValue(yearSliderValue[1])}
          </div>
        </div>
        :
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </div>
      }
      <SubTitle>
        <h3>Select Items</h3>
        <p>Selected: {totalSize.toFixed(3)} GB / 10 GB</p>
      </SubTitle>
      <Border />
      {loader ?
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "54vh" }}>
          <CircularProgress />
        </div> :
        <DownloadList>
          {selectedItemIDs.map((item, index) => (
            <DownloadItem key={item}>
              <span className={selectedItemIDs.includes(item) ? 'selected' : ''} onClick={() => handleItemClick(item)} style={{ width: '100%' }}>
                {item}
              </span>
              <div className="actions custom-checkbox">
                <input type="checkbox" id={`checkbox-` + index} checked={selectedItemIDs.includes(item)} onChange={() => handleItemCheckbox(item)} />
                <label htmlFor={`checkbox-` + index} />
              </div>
            </DownloadItem>
          ))}
          {items?.map((item, index) => (
            selectedItemIDs.includes(item) ? null :
              <DownloadItem key={item}>
                <span className={selectedItemIDs.includes(item) ? 'selected' : ''} onClick={() => handleItemClick(item)} style={{ width: '100%' }}>
                  {item}
                </span>
                <div className="actions custom-checkbox">
                  <input type="checkbox" id={`checkbox-` + (index + selectedItemIDs.length)} checked={selectedItemIDs.includes(item)} onChange={() => handleItemCheckbox(item)}/>
                  <label htmlFor={`checkbox-` + (index + selectedItemIDs.length)} />
                </div>
              </DownloadItem>
          ))}
        </DownloadList>
      }
      <Border />
      <div style={{ fontSize: "1.3rem", display: 'flex', alignItems: 'center', height: 30 }}>
        <div>Rows per page:</div>
        <Select value={itemsPerPage} style={{ height: '100%', marginLeft: 20, color: "white" }} onChange={(evt) => setItemsPerPage(evt.target.value)}>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
          <MenuItem value={100}>100</MenuItem>
        </Select>
        {totalItems === 0 ? null :
          <div style={{ marginLeft: 20 }}>
            {itemsStartOffset + 1} - {itemsStartOffset + itemsPerPage <= totalItems ? itemsStartOffset + itemsPerPage : totalItems} of {totalItems}
          </div>
        }
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
          <div>
            <TooltipIconButton title="Previous" ariaLabel="Previous" size={15} onClick={prevPage}>
              <ChevronLeft />
            </TooltipIconButton>
          </div>
          <div style={{ marginLeft: 10 }}>
            <TooltipIconButton title="Next" ariaLabel="Next" size={15} onClick={nextPage}>
              <ChevronRight />
            </TooltipIconButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default CollectionDetail;